import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {TranslocoService} from '@jsverse/transloco';
import * as wjcCore from '@mescius/wijmo';
import {DataService} from '@cosbeni/database';
import {ActivePromotionConfig} from '../config/active-promotion.config';

export interface ApLang {
    id: string;
    label: string;
    flag: string;
}

/** 選べる言語。flag は assets/img/flags/{flag}.svg。 */
export const AP_LANGS: ApLang[] = [
    {id: 'ja', label: '日本語', flag: 'JP'},
    {id: 'en', label: 'English', flag: 'US'},
];

const STORAGE_KEY = 'ap_language';
const RID = /^#\d+:\d+$/;

/**
 * 言語の切替と保存。
 *
 * <p>ira-groupnet.app の languages.component と同じく、ログイン中はユーザごとに
 * AP_Preference (name = 'language') へ保存し、次回ログイン時にその言語で開く。
 * ログイン前 (ログイン画面など) でも前回の言語で出せるよう端末にも保存する。
 * Wijmo の culture (日付・数値書式、グリッドの文言) も一緒に切り替える。
 */
@Injectable({providedIn: 'root'})
export class LanguageService {
    private userRid: string = null;

    constructor(private transloco: TranslocoService, private dataService: DataService) {
    }

    get langs(): ApLang[] {
        return AP_LANGS;
    }

    get active(): string {
        return this.transloco.getActiveLang();
    }

    /** 起動時: 端末に保存した言語 → ブラウザの言語 → 日本語 の順で決めて読み込む。 */
    init(): Observable<any> {
        const lang = this.normalize(this.stored()) || this.normalize(this.browserLang()) || 'ja';
        this.apply(lang);
        return this.transloco.load(lang).pipe(catchError(() => of(null)));
    }

    /** ログインユーザが分かったら、そのユーザの保存済み言語に合わせる。 */
    useUser(user: any): void {
        const rid = wjcCore.isObject(user) ? user['@rid'] : null;
        if (!RID.test(rid || '') || rid === this.userRid) {
            return;
        }
        this.userRid = rid;
        this.dataService.query({
            sql: "SELECT value FROM AP_Preference WHERE name = 'language' AND user = " + rid,
        }, ActivePromotionConfig.SERVER_URL).pipe(
            map((data: any) => wjcCore.isArray(data && data.results) && data.results.length > 0 ? data.results[0].value : null),
            catchError(() => of(null)),
        ).subscribe((value: string) => {
            const lang = this.normalize(value);
            if (lang && lang !== this.active) {
                this.apply(lang);
            }
        });
    }

    /** 画面から言語を選んだとき。ログイン中ならユーザの設定として保存する。 */
    setLang(lang: string): void {
        lang = this.normalize(lang);
        if (!lang) {
            return;
        }
        this.apply(lang);
        if (this.userRid) {
            const sql = 'BEGIN\n'
                + "UPDATE AP_Preference SET name = 'language', value = '" + lang + "', user = " + this.userRid
                + " UPSERT WHERE name = 'language' AND user = " + this.userRid + '\n'
                + 'COMMIT RETRY 100\n';
            this.dataService.batch({sql: sql}, ActivePromotionConfig.SERVER_URL).subscribe({error: () => undefined});
        }
    }

    /** ログアウトしたら次のユーザの設定を読み直せるようにする。 */
    clearUser(): void {
        this.userRid = null;
    }

    private apply(lang: string): void {
        this.transloco.setActiveLang(lang);
        document.documentElement.lang = lang;
        try {
            localStorage.setItem(STORAGE_KEY, lang);
        } catch (e) {
            // 端末保存できなくても切替自体は続ける
        }
        // en / ja の culture は main.ts で読み込み済み。読み込み済みの culture に差し替えて全コントロールを再描画する。
        wjcCore.changeCultureSync(lang);
    }

    private normalize(lang: string): string {
        return AP_LANGS.some((l) => l.id === lang) ? lang : null;
    }

    private stored(): string {
        try {
            return localStorage.getItem(STORAGE_KEY);
        } catch (e) {
            return null;
        }
    }

    private browserLang(): string {
        const lang = (navigator.language || '').toLowerCase();
        return lang.split('-')[0];
    }
}
